import type { LDContext, LDClient } from "launchdarkly-js-client-sdk";
import { v4 as uuid } from "uuid";
import { Config } from "@/shared/config";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useUserStore } from "@/shared/store/user";
import { watch } from "@vue/composition-api";
import { isProd } from "@/shared/env";

// Flags need to be manually created in LaunchDarkly with the "Portal." prefix
const flagsContainer: { [key: string]: boolean } = {
  overrideHealthPlanIsLinked: false,
  showFundingMethod: false,
  bend15691OrderByRelevance: false,
  showBulkEnrollment: false,
  drivewealthAllowManagedSignup: false,
  pauseOnboardingForUnclaimedSelfReportedEmployees: false,
  lobAddressValidationIsEnabled: false,
  showDynamicAllegacyFees: false,
  hsa4475allowRolePermissions: false,
  bend15789NotionalTransactions: false,
  bend20582UseGetPlansAndBalances: false,
  bend15878DebitCardManagementUi: false,
  bend22264EnableMassTransitPlanSettings: false,
  bend16025ShowHsabAccountCardChart: false,
  bend16364HsabResourceNavigation: false,
  bend16362ResourceFormsInEmployer: false,
  bend16780CombinedAccountSummariesEnabled: false,
  bend16311ResourceFormsInEmployee: false,
  bend21840ShowFeeScheduleAndRatesDeepLink: false,
    bend25422ShowFeeScheduleAndRatesModal: false,
  bend25434EnableNotificationPreferences: false,
  bend16322EnableHsabTransactions: false,
  bend16400HsabNotionalFileAClaim: false,
  bend16612ContributionHandling: false,
  bend24268FinicityEnabledForHsab: false,
  bend23473DisplayLinkedAccountsForHsab: false,
  bend20386UnlinkBankAccountsEnabled: false,
  bend17151ContributionWidgetUpdate: false,
  bend18282ClaimSsoLinkWithParam: false,
  bend18282ClaimSsoLinkDashboard: false,
  bend18604NotionalAccountSummaries: false,
  hsa5812ConsumerLiveChat: false,
  bend18761AddDeepLinkToResourcesPages: false,
  bend19788FormDocumentLinksEnabled: false,
  bend20105ExpenseFilters: false,
  bend22995ShowTalonClaimsInExpenseManagement: false,
  bend23030ClaimLinkAndCreateFeatureEnabled: false,
  bend17261EnableProfileAddEdit: false,
  bend19874ExpensePayeeAddressLob: false,
  bend21410DisplayHsaBalanceOnExpenseManagement: false,
  bend24837AllowMerchantAutoComplete: false,
  hsa6356EnableHsabAdvisorAlerts: false,
  bend19525ConsumerClaimsExchangeSettings: false,
  bend20914DeepLinkForManageHealthcareClaims: false,
  bend21900ExportAllExpenses: false,
  bend21900ExportFilteredExpenses: false,
  bend22263MassTransitExpenseCategory: false,
  bend22628RequireSubtype: false,
  bend19850ProfileCoverageLevelUpdates: false,
  bend17254ProfileLobAddressValidation: false,
  bend22421ShowMessageForSelect: false,
  bend22211DevenirNonCompliantUserMessageDisplay: false,
  bend21901HidePartnerDebitCardSettings: false,
  bend22847EnableV2AccountHolderDashboard: false,
  bend23571AllowValidPreHsaStartDateExpenses: false,
  partnerBulkActions: false,
  bend18227EnableRepaymentConfirmation: false,
  bend24505HideInvestmentAccountNumberMaskButtonForImpersonation: false,
  bend21504EnableContentTypes: false,
  hsa7246AdminInitialUserCreation: false,
  bend23519EnablePartnerInvestmentInstrumentAllowSell: false,
  bend23706DisplayBulkUploadForm: false,
  bend24348HSAClosureReason: false,
  bend24547ExcludeHsaFileClaim: false,
  bend24088EnableDrivewealthOrderCancelButton: false,
  bend24349EnableBuySellByShare: false,
  bend24350DisplayAddDWAccountButtonOnMultiAccountSelectView: false,
  bend23884EmpPortalOktaLogin: false,
  bend24844UploadShoeboxReceipts: false,
  bend24761DisplayInstrumentPerformanceView: false,
  bend23845EnableAccountHolderExpenses: false,
  bend22299Fdic: false,
  hsa7793MessagingCenter: false,
  bend25653EnableDrivewealthPerformance: false,
  bend25644EnableSummaryDashboard: false,
  bend26163AdminPortalConsumer : false,
  bend26732EnableSearchByEmployeeId: false,
  bend25871FraudAdminCanManageLinkedBankAccounts: false,
  bend26150AdminCardErGetsNewCardsOption: false
};

const alwaysDefaultInProdContainer: { [key: string]: boolean } = {
  UseDemoDataInjectionTableForDrivewealthMocks: false
};

type FeatureFlagContainer = typeof flagsContainer;
type AlwaysDefaultInProdContainer = typeof alwaysDefaultInProdContainer;

export type FeatureFlags = FeatureFlagContainer & AlwaysDefaultInProdContainer;
export const defaultFlagState = { ...flagsContainer, ...alwaysDefaultInProdContainer};


let client: LDClient | null;

export async function initialize () {
  const featureFlagStore = useFeatureFlagStore();
  const context = buildContext();
  client = LDClient.initialize(Config.LaunchDarklyClientId, context, {
    streaming: true
  });

  try {
      await client.waitForInitialization(5);
  } catch (err) {
    featureFlagStore.setTimedOut();
  }

  await loadFlags();

  client.on("change", settings => {
    for (const key in settings) {
      featureFlagStore.updateFlag(parseFlagKey(key), settings[key].current);
    }
  });

  watch(() => featureFlagStore.context, identify);
}

async function loadFlags () {
  const featureFlagStore = useFeatureFlagStore();
  const flagRecords: Record<string, boolean> = {};

  Object.keys(flagsContainer).forEach(key => {
    flagRecords[key] = client.variation(`Portal.${key}`, flagsContainer[key]);
  });

  // AlwaysDefaultInProd flags are only loaded from LaunchDarkly in non-production
  Object.keys(alwaysDefaultInProdContainer).forEach(key => {
    flagRecords[key] = isProd() ? alwaysDefaultInProdContainer[key] : client.variation(`AlwaysDefaultInProd.${key}`, flagsContainer[key]);
  });

  featureFlagStore.setFlags(flagRecords);
}

function parseFlagKey (key: string): string {
  const portalPrefix = "Portal.";
  const alwaysDefaultInProdPrefix = "AlwaysDefaultInProd.";
  return key.replace(new RegExp(`^${portalPrefix}`), "").replace(new RegExp(`^${alwaysDefaultInProdPrefix}`), "");
}

async function identify () {
  const context = buildContext();
  client.waitForInitialization(5);
  await client.identify(context, null);
  await loadFlags();
}

function buildContext (): LDContext  {
  const featureFlagStore = useFeatureFlagStore();
  const userStore = useUserStore();
  if (!userStore.userId) {
    return {
      kind: "user",
      // The key for anonymous users must be hardcoded (per LaunchDarkly),
      // otherwise LD counts anonymous users in billing, which means being billed more than 2x per user
      key: "anonymous_user",
      anonymous: true
    };
  }

  const context: any = {
    kind: "user",
    key: userStore.userId,
    email: featureFlagStore.context.email,
  };

  if (featureFlagStore.context.employerId) {
    context.employerId = featureFlagStore.context.employerId;
  }

  if (featureFlagStore.context.partnerId) {
    context.partnerId = featureFlagStore.context.partnerId;
  }

  if (featureFlagStore.context.custodian) {
    context.custodian = featureFlagStore.context.custodian;
  }
  return context;
}

export default {
  initialize
};